import React, { useEffect, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import { initWorkShift } from '../../model/sale';
import { getToken, isEmpty } from '../../utils/util';
import { api_work_shift } from '../../api/sale';
import { ExclamationCircleFilled, PrinterOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { decode as base64_decode } from 'js-base64';

interface props {
    lock: boolean,
    onSaved: () => void,
    onClosed: () => void,
}

const WorkShift: React.FC<props> = (prop) => {

    const [data, setData] = useState<any>({...initWorkShift});
    const [title, setTitle] = useState('เปิดกะการทำงาน');
    const [isOpenShift, setIsOpenShift] = useState(true); // true = Opening shift, false = Closing shift
    const [shiftInfo, setShiftInfo] = useState<any>(null);

    // Banknote amounts in Baht (เงินรวมของแต่ละชนิด)
    const [amt1000, setAmt1000] = useState<number | string>(0);
    const [amt500, setAmt500] = useState<number | string>(0);
    const [amt100, setAmt100] = useState<number | string>(0);
    const [amt50, setAmt50] = useState<number | string>(0);
    const [amt20, setAmt20] = useState<number | string>(0);

    // Coin amounts in Baht (เงินรวมของแต่ละชนิด)
    const [amt10, setAmt10] = useState<number | string>(0);
    const [amt5, setAmt5] = useState<number | string>(0);
    const [amt2, setAmt2] = useState<number | string>(0);
    const [amt1, setAmt1] = useState<number | string>(0);
    const [amt050, setAmt050] = useState<number | string>(0);
    const [amt025, setAmt025] = useState<number | string>(0);

    // Totals
    const [banknoteTotal, setBanknoteTotal] = useState(0);
    const [coinTotal, setCoinTotal] = useState(0);
    const [totalCounted, setTotalCounted] = useState(0);

    // Shift Closing Metrics
    const [openingFloat, setOpeningFloat] = useState(0);
    const [cashSales, setCashSales] = useState(0);
    const [otherSales, setOtherSales] = useState(0);
    const [totalBills, setTotalBills] = useState(0);
    const [expectedCash, setExpectedCash] = useState(0);

    const init = async () => {
        let token = '';
        await getToken().then((item: string) => {
            token = item;
        });

        let getWorkShift: any = await api_work_shift('GET_BY_CONDITION', {'device': token});
        
        if (getWorkShift && getWorkShift['id'] > 0 && getWorkShift['status'] === 1) {
            // Currently OPEN -> this modal is for CLOSING shift
            setTitle('ปิดกะการทำงาน');
            setIsOpenShift(false);
            setShiftInfo(getWorkShift);

            let opFloat = Number(getWorkShift.opening_float || getWorkShift.total || 0);
            let cSales = Number(getWorkShift.cash_sales || 0);
            let oSales = Number(getWorkShift.other_sales || 0);
            let tBills = Number(getWorkShift.total_bills || 0);
            let expCash = Number(getWorkShift.expected_cash || (opFloat + cSales));

            setOpeningFloat(opFloat);
            setCashSales(cSales);
            setOtherSales(oSales);
            setTotalBills(tBills);
            setExpectedCash(expCash);

            let exp_data: any = {...initWorkShift};
            exp_data['expected'] = expCash;
            setData(exp_data);
        } else {
            // Currently CLOSED or New -> this modal is for OPENING shift
            setTitle('เปิดกะการทำงาน');
            setIsOpenShift(true);
            setShiftInfo(null);
            setOpeningFloat(0);
            setCashSales(0);
            setOtherSales(0);
            setTotalBills(0);
            setExpectedCash(0);

            let initialData: any = {...initWorkShift};
            if (getWorkShift && getWorkShift['id'] > 0) {
                // Carry over previous shift's final cash converted to amounts in Baht
                let a1000 = (getWorkShift.c1000 || 0) * 1000;
                let a500 = (getWorkShift.c500 || 0) * 500;
                let a100 = (getWorkShift.c100 || 0) * 100;
                let a50 = (getWorkShift.c50 || 0) * 50;
                let a20 = (getWorkShift.c20 || 0) * 20;
                let a10 = (getWorkShift.c10 || 0) * 10;
                let a5 = (getWorkShift.c5 || 0) * 5;
                let a2 = (getWorkShift.c2 || 0) * 2;
                let a1 = (getWorkShift.c1 || 0) * 1;
                let a050 = (getWorkShift.c050 || 0) * 0.5;
                let a025 = (getWorkShift.c025 || 0) * 0.25;

                setAmt1000(a1000);
                setAmt500(a500);
                setAmt100(a100);
                setAmt50(a50);
                setAmt20(a20);
                setAmt10(a10);
                setAmt5(a5);
                setAmt2(a2);
                setAmt1(a1);
                setAmt050(a050);
                setAmt025(a025);

                recalculateAmounts(a1000, a500, a100, a50, a20, a10, a5, a2, a1, a050, a025);
            } else {
                setData(initialData);
                recalculateAmounts(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            }
        }
    };

    const handleAmountChange = (field: string, val: string) => {
        let numVal: number | string = val === '' ? '' : Math.max(0, parseFloat(val) || 0);

        let new1000 = field === 'amt1000' ? numVal : amt1000;
        let new500  = field === 'amt500'  ? numVal : amt500;
        let new100  = field === 'amt100'  ? numVal : amt100;
        let new50   = field === 'amt50'   ? numVal : amt50;
        let new20   = field === 'amt20'   ? numVal : amt20;
        let new10   = field === 'amt10'   ? numVal : amt10;
        let new5    = field === 'amt5'    ? numVal : amt5;
        let new2    = field === 'amt2'    ? numVal : amt2;
        let new1    = field === 'amt1'    ? numVal : amt1;
        let new050  = field === 'amt050'  ? numVal : amt050;
        let new025  = field === 'amt025'  ? numVal : amt025;

        switch (field) {
            case 'amt1000': setAmt1000(numVal); break;
            case 'amt500':  setAmt500(numVal); break;
            case 'amt100':  setAmt100(numVal); break;
            case 'amt50':   setAmt50(numVal); break;
            case 'amt20':   setAmt20(numVal); break;
            case 'amt10':   setAmt10(numVal); break;
            case 'amt5':    setAmt5(numVal); break;
            case 'amt2':    setAmt2(numVal); break;
            case 'amt1':    setAmt1(numVal); break;
            case 'amt050':  setAmt050(numVal); break;
            case 'amt025':  setAmt025(numVal); break;
            default: break;
        }

        recalculateAmounts(new1000, new500, new100, new50, new20, new10, new5, new2, new1, new050, new025);
    };

    const recalculateAmounts = (
        a1000: any, a500: any, a100: any, a50: any, a20: any,
        a10: any, a5: any, a2: any, a1: any, a050: any, a025: any
    ) => {
        let sumBanknotes = (Number(a1000) || 0) + (Number(a500) || 0) + (Number(a100) || 0) + (Number(a50) || 0) + (Number(a20) || 0);
        let sumCoins = (Number(a10) || 0) + (Number(a5) || 0) + (Number(a2) || 0) + (Number(a1) || 0) + (Number(a050) || 0) + (Number(a025) || 0);

        setBanknoteTotal(sumBanknotes);
        setCoinTotal(sumCoins);
        setTotalCounted(sumBanknotes + sumCoins);
    };

    // Helper: calculate number of notes / coins from amount
    const getPiecesCount = (amt: any, denom: number) => {
        let val = Number(amt) || 0;
        if (val <= 0) return 0;
        let count = val / denom;
        return Number.isInteger(count) ? count : count.toFixed(2);
    };

    const getConfigModal = (titleStr: string, contentStr: string) => {
        return {
            title: titleStr,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: contentStr,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        };
    };

    const onPrintReport = () => {
        window.print();
    };

    const onSave = async () => {
        let uuid = isEmpty(window.localStorage.getItem("uuid")) ? "" : window.localStorage.getItem("uuid") + "";
        let user = JSON.parse(base64_decode(uuid) || "{}");
        let token = '';
        await getToken().then((item: string) => {
            token = item;
        });

        // Convert amounts back to piece counts for DB compatibility
        let body: any = {
            device: token,
            create_by: user['id'] || 1,
            e1000: data.e1000 || 0,
            e500: data.e500 || 0,
            e100: data.e100 || 0,
            e50: data.e50 || 0,
            e20: data.e20 || 0,
            e10: data.e10 || 0,
            e5: data.e5 || 0,
            e2: data.e2 || 0,
            e1: data.e1 || 0,
            e050: data.e050 || 0,
            e025: data.e025 || 0,
            c1000: Math.round((Number(amt1000) || 0) / 1000),
            c500:  Math.round((Number(amt500) || 0) / 500),
            c100:  Math.round((Number(amt100) || 0) / 100),
            c50:   Math.round((Number(amt50) || 0) / 50),
            c20:   Math.round((Number(amt20) || 0) / 20),
            c10:   Math.round((Number(amt10) || 0) / 10),
            c5:    Math.round((Number(amt5) || 0) / 5),
            c2:    Math.round((Number(amt2) || 0) / 2),
            c1:    Math.round((Number(amt1) || 0) / 1),
            c050:  Math.round((Number(amt050) || 0) / 0.5),
            c025:  Math.round((Number(amt025) || 0) / 0.25),
            expected: isOpenShift ? totalCounted : expectedCash,
            total: totalCounted,
            status: isOpenShift ? 1 : 0, // 1 = Open, 0 = Close
        };

        Modal.confirm({
            title: `ยืนยันการ${title}`,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: `ยอดเงินสดที่นับได้: ฿ ${totalCounted.toLocaleString('en-US', {minimumFractionDigits: 2})} บาท คุณต้องการบันทึกหรือไม่?`,
            okText: 'บันทึก',
            cancelText: 'ยกเลิก',
            centered: true,
            async onOk() {
                let res = await api_work_shift('INSERT', body);
                if (res === 1) {
                    Modal.success({
                        ...getConfigModal('สำเร็จ', `บันทึก${title}เรียบร้อยแล้ว`),
                        onOk() { prop.onSaved(); }
                    });
                } else {
                    Modal.error({
                        ...getConfigModal('ผิดพลาด', `ไม่สามารถบันทึก${title}ได้ กรุณาลองใหม่อีกครั้ง`)
                    });
                }
            }
        });
    };

    useEffect(() => {
        init();
    }, []);

    const discrepancy = totalCounted - expectedCash;

    return (
        <div className='workShiftWrapper'>
            {/* Header / Mode Indicator */}
            <div className='workShiftHeader'>
                <div className='workShiftBadge' style={{
                    backgroundColor: isOpenShift ? 'var(--secondary-light, #ECFDF5)' : '#FEF2F2',
                    color: isOpenShift ? 'var(--secondary, #10B981)' : '#EF4444',
                    borderColor: isOpenShift ? '#A7F3D0' : '#FECACA'
                }}>
                    <span className='dot' style={{backgroundColor: isOpenShift ? '#10B981' : '#EF4444'}}></span>
                    {isOpenShift ? '🟢 โหมดเปิดกะการทำงาน (Open Shift)' : '🔴 โหมดปิดกะการทำงาน (Close Shift)'}
                </div>
                {shiftInfo && !isOpenShift && (
                    <div className='shiftMetaInfo'>
                        <span>🕒 เปิดกะเมื่อ: <strong>{shiftInfo.formatted_create_date || '-'}</strong></span>
                        <span>👤 ผู้เปิดกะ: <strong>{shiftInfo.employee_name || 'Admin'}</strong></span>
                    </div>
                )}
            </div>

            {/* Shift Closing Summary Cards (Only shown when closing shift) */}
            {!isOpenShift && (
                <div className='shiftMetricsGrid'>
                    <div className='metricCard'>
                        <span className='label'>เงินทอนตั้งต้น</span>
                        <span className='value'>฿ {openingFloat.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className='metricCard'>
                        <span className='label'>ยอดขายเงินสด ({totalBills} บิล)</span>
                        <span className='value' style={{color: 'var(--secondary, #10B981)'}}>+ ฿ {cashSales.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className='metricCard'>
                        <span className='label'>ยอดขายเงินโอน/QR</span>
                        <span className='value' style={{color: 'var(--accent-blue, #3B82F6)'}}>฿ {otherSales.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className='metricCard highlight'>
                        <span className='label'>เงินสดที่ควรมีในลิ้นชัก</span>
                        <span className='value highlight'>฿ {expectedCash.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                </div>
            )}

            {/* 2-Column Denominations Counting Grid (Enter Amount in Baht -> Computed Pieces) */}
            <div className='denominationsGrid'>
                {/* Left: Banknotes */}
                <div className='denomCol'>
                    <div className='denomColHeader'>
                        <span>💵 ธนบัตร (กรอกจำนวนเงินบาท)</span>
                        <span className='subtotal'>฿ {banknoteTotal.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className='denomTable'>
                        <div className='denomRow'>
                            <span className='denomLabel note-1000'>฿ 1,000</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={1000} value={amt1000} placeholder="0" onChange={(e) => handleAmountChange('amt1000', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt1000, 1000)}</strong> ใบ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel note-500'>฿ 500</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={500} value={amt500} placeholder="0" onChange={(e) => handleAmountChange('amt500', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt500, 500)}</strong> ใบ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel note-100'>฿ 100</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={100} value={amt100} placeholder="0" onChange={(e) => handleAmountChange('amt100', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt100, 100)}</strong> ใบ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel note-50'>฿ 50</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={50} value={amt50} placeholder="0" onChange={(e) => handleAmountChange('amt50', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt50, 50)}</strong> ใบ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel note-20'>฿ 20</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={20} value={amt20} placeholder="0" onChange={(e) => handleAmountChange('amt20', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt20, 20)}</strong> ใบ</span>
                        </div>
                    </div>
                </div>

                {/* Right: Coins */}
                <div className='denomCol'>
                    <div className='denomColHeader'>
                        <span>🪙 เหรียญ (กรอกจำนวนเงินบาท)</span>
                        <span className='subtotal'>฿ {coinTotal.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className='denomTable'>
                        <div className='denomRow'>
                            <span className='denomLabel coin'>฿ 10</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={10} value={amt10} placeholder="0" onChange={(e) => handleAmountChange('amt10', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt10, 10)}</strong> เหรียญ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel coin'>฿ 5</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={5} value={amt5} placeholder="0" onChange={(e) => handleAmountChange('amt5', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt5, 5)}</strong> เหรียญ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel coin'>฿ 2</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={2} value={amt2} placeholder="0" onChange={(e) => handleAmountChange('amt2', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt2, 2)}</strong> เหรียญ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel coin'>฿ 1</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={1} value={amt1} placeholder="0" onChange={(e) => handleAmountChange('amt1', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt1, 1)}</strong> เหรียญ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel coin small'>฿ 0.50</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={0.5} value={amt050} placeholder="0" onChange={(e) => handleAmountChange('amt050', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt050, 0.5)}</strong> เหรียญ</span>
                        </div>

                        <div className='denomRow'>
                            <span className='denomLabel coin small'>฿ 0.25</span>
                            <div className='inputWrap'>
                                <Input type="number" min={0} step={0.25} value={amt025} placeholder="0" onChange={(e) => handleAmountChange('amt025', e.target.value)} />
                                <span className='unit'>บาท</span>
                            </div>
                            <span className='pieceBadge'>= <strong>{getPiecesCount(amt025, 0.25)}</strong> เหรียญ</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Final Summary & Discrepancy Display */}
            <div className='shiftFinalSummaryBox'>
                <div className='summaryItem'>
                    <span className='title'>ยอดเงินสดที่นับได้จริง</span>
                    <span className='amount main'>฿ {totalCounted.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                </div>

                {!isOpenShift && (
                    <div className='summaryItem'>
                        <span className='title'>ยอดที่ควรมีในลิ้นชัก</span>
                        <span className='amount'>฿ {expectedCash.toLocaleString('en-US', {minimumFractionDigits: 2})}</span>
                    </div>
                )}

                {!isOpenShift && (
                    <div className='discrepancyBadge' style={{
                        backgroundColor: discrepancy === 0 ? '#ECFDF5' : discrepancy < 0 ? '#FEF2F2' : '#FFFBEB',
                        color: discrepancy === 0 ? '#059669' : discrepancy < 0 ? '#DC2626' : '#D97706',
                        borderColor: discrepancy === 0 ? '#A7F3D0' : discrepancy < 0 ? '#FECACA' : '#FDE68A'
                    }}>
                        {discrepancy === 0 ? (
                            <span><CheckCircleOutlined rev={undefined} /> ยอดเงินถูกต้อง ตรงตามระบบ (฿ 0.00)</span>
                        ) : discrepancy < 0 ? (
                            <span><WarningOutlined rev={undefined} /> เงินขาด: - ฿ {Math.abs(discrepancy).toLocaleString('en-US', {minimumFractionDigits: 2})} บาท</span>
                        ) : (
                            <span><CheckCircleOutlined rev={undefined} /> เงินเกิน: + ฿ {discrepancy.toLocaleString('en-US', {minimumFractionDigits: 2})} บาท</span>
                        )}
                    </div>
                )}
            </div>

            {/* Action Tools */}
            <div className='workShiftFooterTools'>
                {!isOpenShift && (
                    <Button className='btn-cancel' onClick={onPrintReport} style={{marginRight: 'auto'}}>
                        <PrinterOutlined rev={undefined} /> พิมพ์ใบสรุปกะ (X-Report)
                    </Button>
                )}
                {!prop.lock && (
                    <Button className='btn-cancel' onClick={prop.onClosed}>
                        ยกเลิก
                    </Button>
                )}
                <Button className='btn-save' type="primary" onClick={onSave} style={{minWidth: '130px'}}>
                    {isOpenShift ? '🟢 บันทึกเปิดกะ' : '🔴 บันทึกปิดกะ'}
                </Button>
            </div>
        </div>
    );
};

export default WorkShift;